import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import FlexBox from "../../../../components/layouts/flexbox";
import {
  INITIATIVE_TYPE_OPTIONS,
  INITIAL_INITIATIVES_FILTER_VALUES,
  getHasActiveInitiativesFilters,
  normalizeInitiativesFilterValues,
  type InitiativesFilterValues,
} from "./utils";

const VISIBILITY_OPTIONS = [
  { value: "", label: "All" },
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

function InitiativesFilters({
  onChange,
  disabled = false,
}: {
  onChange: (filters: InitiativesFilterValues) => void;
  disabled?: boolean;
}) {
  const [filters, setFilters] = useState<InitiativesFilterValues>(
    INITIAL_INITIATIVES_FILTER_VALUES
  );

  const hasActiveFilters = getHasActiveInitiativesFilters(filters);

  const applyFilters = (next: InitiativesFilterValues) => {
    onChange(normalizeInitiativesFilterValues(next));
  };

  const handleTitleChange = (value: string) => {
    const next = { ...filters, title: value };
    setFilters(next);
    applyFilters(next);
  };

  const handleTypeChange = (value: string) => {
    const next = { ...filters, type: value };
    setFilters(next);
    applyFilters(next);
  };

  const handleVisibilityChange = (value: string) => {
    const next = { ...filters, visibility: value };
    setFilters(next);
    applyFilters(next);
  };

  const handleClear = () => {
    setFilters(INITIAL_INITIATIVES_FILTER_VALUES);
    onChange(INITIAL_INITIATIVES_FILTER_VALUES);
  };

  return (
    <Grid container alignItems="center" spacing={1}>
      <Grid item xs={12}>
        <Typography fontSize={13} fontWeight={600} color="#374151">
          Search
        </Typography>
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <TextField
          size="small"
          fullWidth
          placeholder="Title"
          value={filters.title}
          disabled={disabled}
          onChange={(e) => handleTitleChange(e.target.value)}
          sx={{ bgcolor: "#FFFFFF" }}
        />
      </Grid>
      <Grid item xs={12} md={3} lg={2}>
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={filters.type}
          disabled={disabled}
          onChange={(e) => handleTypeChange(e.target.value)}
          sx={{ bgcolor: "#FFFFFF" }}
          renderValue={(value) =>
            value ? (
              INITIATIVE_TYPE_OPTIONS.find((option) => option.value === value)
                ?.label ?? "All"
            ) : (
              <Typography component="span" fontSize={14} color="#9CA3AF">
                Type
              </Typography>
            )
          }
        >
          <MenuItem value="">
            <Typography fontSize={14} color="#9CA3AF">
              All
            </Typography>
          </MenuItem>
          {INITIATIVE_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12} md={3} lg={2}>
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={filters.visibility}
          disabled={disabled}
          onChange={(e) => handleVisibilityChange(e.target.value)}
          sx={{ bgcolor: "#FFFFFF" }}
          renderValue={(value) =>
            value ? (
              VISIBILITY_OPTIONS.find((option) => option.value === value)?.label
            ) : (
              <Typography component="span" fontSize={14} color="#9CA3AF">
                Public / Private
              </Typography>
            )
          }
        >
          {VISIBILITY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      {hasActiveFilters && (
        <Grid item xs={12} md={2}>
          <FlexBox alignItems="center" height="100%">
            <Button size="small" onClick={handleClear} disabled={disabled}>
              Clear all
            </Button>
          </FlexBox>
        </Grid>
      )}
    </Grid>
  );
}

export { InitiativesFilters };
